import { useState, type Ref } from 'react';
import { ChevronLeft, ChevronRight, ListFilter, Plus } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@reflex/ui/components/ui/command';
import { FilterOptionList } from './FilterOptionList';
import { FILTER_ALL_VALUE, isFilterApplied, type FilterDefinition } from './filter-types';

interface AddFilterButtonProps {
  /** All filter definitions — applied and unapplied. */
  filters: FilterDefinition[];
  /** Resets every filter. Wired to the Clear button in the footer. */
  onClear?: () => void;
  /** Forwarded to the trigger so the bar can move focus here. */
  ref?: Ref<HTMLButtonElement>;
  /**
   * Collapse the trigger to a bare `+`. Set once chips are on screen, where
   * they already say what the control is for and the words are only width.
   */
  compact?: boolean;
  className?: string;
}

/**
 * Opens a filter popover showing quick-toggle chips, a searchable category
 * list with active-count badges, and a footer with Clear / Done actions.
 *
 * Selecting a category drills into its value picker; picking a value returns
 * to the category list so multiple filters can be composed before closing.
 */
export function AddFilterButton({
  filters,
  onClear,
  compact = false,
  className,
  ref,
}: AddFilterButtonProps) {
  const [open, setOpen] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const empty = filters.length === 0;

  const pending = filters.find((d) => d.id === pendingId) ?? null;

  // Boolean on/off facets rendered as pill toggles above the category list.
  const quickToggles = filters.filter((d) => d.quickToggle);
  // All other facets — shown as navigable rows in the list.
  const categories = filters.filter((d) => !d.quickToggle);
  const activeCount = filters.filter(isFilterApplied).length;

  const close = () => {
    setOpen(false);
    setPendingId(null);
  };

  const trigger = (
    <PopoverTrigger asChild>
      <button
        ref={ref}
        type="button"
        aria-label="Add filter"
        disabled={empty}
        data-testid="add-filter-trigger"
        className={cn(
          'flex h-7 shrink-0 items-center gap-1 rounded-md border border-dashed border-border px-2 text-xs transition-colors',
          'text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'enabled:hover:border-ring/40 enabled:hover:bg-accent enabled:hover:text-foreground',
          'disabled:cursor-not-allowed disabled:opacity-40',
          open && 'border-ring/40 bg-accent text-foreground',
          compact && 'px-1.5',
          className,
        )}
      >
        {compact ? (
          <Plus className="size-3.5 shrink-0" aria-hidden="true" />
        ) : (
          <>
            <ListFilter className="size-3.5 shrink-0" aria-hidden="true" />
            <span>Add filter</span>
          </>
        )}
      </button>
    </PopoverTrigger>
  );

  return (
    <Popover
      open={open && !empty}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setPendingId(null);
      }}
    >
      {compact || empty ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent side="bottom">
            {empty ? 'No filters available' : 'Add filter'}
          </TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}

      <PopoverContent align="start" className="w-[272px] p-0">
        {pending ? (
          <>
            <div className="flex items-center gap-2 border-b border-border p-3">
              <button
                type="button"
                aria-label="Back to filters"
                onClick={() => setPendingId(null)}
                className="flex size-4 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ChevronLeft className="size-4" aria-hidden="true" />
              </button>
              <span className="text-[12px] font-normal uppercase tracking-[0.06em] text-muted-foreground">
                {pending.label}
              </span>
            </div>
            <FilterOptionList
              definition={pending}
              onBack={() => setPendingId(null)}
              onSelect={(value) => {
                pending.onValueChange(value);
                // Return to the category list so the user can compose more
                // filters before dismissing with Done.
                setPendingId(null);
              }}
            />
          </>
        ) : (
          <Command>
            {/* Search -------------------------------------------------------- */}
            <CommandInput placeholder="Filter by…" className="h-10 text-[13px]" />

            {/* Quick-toggle chips -------------------------------------------- */}
            {quickToggles.length > 0 && (
              <div className="flex flex-wrap gap-[5px] border-b border-border p-2">
                {quickToggles.map((def) => {
                  const active = isFilterApplied(def);
                  return (
                    <button
                      key={def.id}
                      type="button"
                      data-testid={`add-filter-quick-toggle-${def.id}`}
                      onClick={() =>
                        def.onValueChange(
                          active ? FILTER_ALL_VALUE : (def.options[0]?.value ?? FILTER_ALL_VALUE),
                        )
                      }
                      className={cn(
                        'flex h-[26px] items-center rounded-full border px-[9px] text-[11.5px] leading-[17px] transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        active
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border text-muted-foreground hover:border-ring/50 hover:text-foreground',
                      )}
                    >
                      {def.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Category list ------------------------------------------------- */}
            <CommandList className="max-h-72 p-2">
              <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">
                No matches
              </CommandEmpty>
              <CommandGroup className="p-0">
                {categories.map((def) => {
                  const Icon = def.icon;
                  const applied = isFilterApplied(def);
                  return (
                    <CommandItem
                      key={def.id}
                      value={def.label}
                      onSelect={() => setPendingId(def.id)}
                      className="h-9 gap-2 rounded-[7px] px-2 py-0 text-[13px]"
                      data-testid={`add-filter-option-${def.id}`}
                    >
                      {Icon && (
                        <Icon
                          className="size-4 shrink-0 text-muted-foreground"
                          aria-hidden="true"
                        />
                      )}
                      <span className="flex-1 truncate">{def.label}</span>
                      {applied && (
                        <span className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-primary text-[12px] font-medium leading-none text-primary-foreground">
                          1
                        </span>
                      )}
                      <ChevronRight className="size-4 shrink-0 opacity-40" aria-hidden="true" />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>

            {/* Footer -------------------------------------------------------- */}
            <div className="flex items-center gap-2 border-t border-border px-[10px] py-2">
              <span className="min-w-0 flex-1 text-[11px] text-muted-foreground">
                {activeCount > 0 ? `${activeCount} active` : 'No active filters'}
              </span>
              <button
                type="button"
                onClick={() => onClear?.()}
                disabled={activeCount === 0}
                className={cn(
                  'flex h-[26px] shrink-0 items-center rounded-[6px] border border-border px-[10px] text-xs text-popover-foreground transition-colors',
                  'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  'disabled:cursor-not-allowed disabled:opacity-40',
                )}
              >
                Clear
              </button>
              <button
                type="button"
                onClick={close}
                className={cn(
                  'flex h-[26px] shrink-0 items-center rounded-[6px] bg-primary px-[10px] text-xs text-primary-foreground transition-colors',
                  'hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
              >
                Done
              </button>
            </div>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}
